import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePollingFallback } from '@/hooks/usePollingFallback';
import { useVisibility } from '@/hooks/useVisibility';
import { startOfDayInTz, endOfDayInTz, formatDateInputInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';

export interface DailyHuddleMetrics {
    revenue: number;
    jobsCancelled: number;
    avgTicket: number;
    bookingRate: number;
    topCancellationReason: string;
}

export const useDailyHuddle = (timezone: string = DEFAULT_TIMEZONE) => {
    const refetchInterval = usePollingFallback(5 * 60_000);
    const { canSeeCompanyFinancials } = useVisibility();

    return useQuery({
        queryKey: ['daily-huddle', timezone],
        refetchInterval,
        enabled: canSeeCompanyFinancials,
        queryFn: async () => {
            const now = new Date();
            const startOfDay = startOfDayInTz(now, timezone).toISOString();
            const endOfDay = endOfDayInTz(now, timezone).toISOString();
            const todayDateStr = formatDateInputInTz(now, timezone);

            console.log('Fetching Daily Huddle for:', startOfDay, 'to', endOfDay);

            // 1. Fetch completed jobs with job_date = today (business close date)
            const { data: jobsClosedToday, error: closedError } = await supabase
                .from('jobs')
                .select('invoice_total')
                .eq('status', 'completed')
                .eq('job_date', todayDateStr);

            if (closedError) throw closedError;

            // 2. Fetch cancelled jobs CANCELLED today (server-side date filter)
            const { data: jobsCancelledToday, error: cancelledError } = await supabase
                .from('jobs')
                .select('cancellation_reason')
                .eq('status', 'cancelled')
                .gte('cancelled_at', startOfDay)
                .lte('cancelled_at', endOfDay);

            if (cancelledError) throw cancelledError;

            // 3. Fetch Leads created Today for Booking %
            const { data: leads, error: leadsError } = await supabase
                .from('leads')
                .select('id, status, created_at')
                .gte('created_at', startOfDay)
                .lte('created_at', endOfDay);

            if (leadsError) throw leadsError;

            // --- Calculate Metrics ---

            const totalRevenue = jobsClosedToday.reduce((sum, j) => sum + (j.invoice_total || 0), 0);
            const avgTicket = jobsClosedToday.length > 0 ? totalRevenue / jobsClosedToday.length : 0;

            // Booking Rate = Converted Leads Today / Total Leads Today
            const totalLeadsToday = leads?.length || 0;
            const convertedLeadsToday = leads?.filter(l => l.status === 'converted' || l.status === 'qualified').length || 0;
            // Note: qualified often leads to booking, checking if 'converted' is the only success state

            const bookingRate = totalLeadsToday > 0 ? (convertedLeadsToday / totalLeadsToday) * 100 : 0;

            // Top Cancellation Reason
            const reasonCounts: Record<string, number> = {};
            jobsCancelledToday.forEach(j => {
                const reason = j.cancellation_reason || 'Unknown';
                if (reason) {
                    reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
                }
            });

            // Sort by count desc
            const sortedReasons = Object.entries(reasonCounts).sort(([, a], [, b]) => b - a);
            const topCancellationReason = sortedReasons.length > 0 ? sortedReasons[0][0] : 'None';

            return {
                revenue: totalRevenue,
                jobsCancelled: jobsCancelledToday.length,
                avgTicket,
                bookingRate,
                topCancellationReason,
            };
        },
    });
};
